/** A small reader for simple HTML (the signature HTML and role round, 2026-10-28; brief docs/briefs/signature-html-role-round.md, rule 2 and
 *  "After the red team's review"). Pure, no DOM and no HTML library, so the browser bundle and the server read a signature the same way.
 *  It splits a text into pieces of text and tags, the way a browser would for the simple cases a signature uses:
 *  - a `<` opens a tag only when letters follow and then a space, `/` or `>` (so "Christian <christian@kriesi.at>" and "1 < 2" are text);
 *  - comments, CDATA sections and declarations are pieces of their own (`skip`), which every reader drops;
 *  - the words inside `script`, `style` and their kind are skipped with the tag (a script's words never show);
 *  - an attribute's value is kept as typed (`raw`) and as read (`value`, entities decoded).
 *  The allow-list, the fill and the plain reading build on these pieces (signatureHtml.ts, signature.ts, plainReading.ts). */

export type HtmlAttr = { name: string; value: string; raw: string; quote: '"' | "'" | "" };
export type HtmlPiece =
  | { kind: "text"; raw: string }
  | { kind: "tag"; name: string; close: boolean; attrs: HtmlAttr[]; raw: string }
  | { kind: "skip"; raw: string };

/** elements whose words are never shown: their content is skipped together with the tag */
const HIDDEN_CONTENT = new Set(["script", "style", "iframe", "noscript", "template", "textarea", "title", "object", "xmp", "noembed", "noframes", "select"]);
const NAME = /^[A-Za-z][A-Za-z0-9-]*/;
const ATTR_NAME = /^[^\s/>"'=]+/;

/** the pieces of an HTML text, in order; joining every piece's `raw` gives the text back */
export function htmlPieces(source: string): HtmlPiece[] {
  const out: HtmlPiece[] = [];
  let at = 0, textFrom = 0;
  const flushText = (to: number) => { if (to > textFrom) out.push({ kind: "text", raw: source.slice(textFrom, to) }); };
  while (at < source.length) {
    const lt = source.indexOf("<", at);
    if (lt < 0) break;
    const special = skipAt(source, lt);
    if (special) { flushText(lt); out.push({ kind: "skip", raw: source.slice(lt, special) }); at = textFrom = special; continue; }
    const tag = tagAt(source, lt);
    if (!tag) { at = lt + 1; continue; }
    flushText(lt);
    out.push(tag.piece);
    at = textFrom = tag.end;
    if (!tag.piece.close && HIDDEN_CONTENT.has(tag.piece.name)) {
      const end = closingEnd(source, tag.end, tag.piece.name);
      if (end > tag.end) out.push({ kind: "skip", raw: source.slice(tag.end, end) });
      at = textFrom = end;
    }
  }
  flushText(source.length);
  return out;
}

/** the end of a comment, CDATA section or declaration starting at `lt`, or 0 when none starts there */
function skipAt(s: string, lt: number): number {
  const endOf = (marker: string, from: number) => { const i = s.indexOf(marker, from); return i < 0 ? s.length : i + marker.length; };
  if (s.startsWith("<!--", lt)) return endOf("-->", lt + 4);
  if (s.slice(lt, lt + 9).toUpperCase() === "<![CDATA[") return endOf("]]>", lt + 9);
  if (/^<[!?][A-Za-z]/.test(s.slice(lt, lt + 3))) return endOf(">", lt + 2);
  return 0;
}

/** a tag starting at `lt`, or null when the `<` is a character */
function tagAt(s: string, lt: number): { piece: Extract<HtmlPiece, { kind: "tag" }>; end: number } | null {
  const close = s[lt + 1] === "/";
  let i = lt + (close ? 2 : 1);
  const name = NAME.exec(s.slice(i, i + 64))?.[0];
  if (!name) return null;
  i += name.length;
  if (i < s.length && !/[\s/>]/.test(s[i]!)) return null;
  const attrs: HtmlAttr[] = [];
  while (i < s.length) {
    while (i < s.length && /[\s/]/.test(s[i]!)) i++;
    if (i >= s.length) return null;
    if (s[i] === ">") return { piece: { kind: "tag", name: name.toLowerCase(), close, attrs: close ? [] : attrs, raw: s.slice(lt, i + 1) }, end: i + 1 };
    const attr = attrAt(s, i);
    if (!attr) return null;
    attrs.push(attr.attr);
    i = attr.end;
  }
  return null;
}

function attrAt(s: string, from: number): { attr: HtmlAttr; end: number } | null {
  const name = ATTR_NAME.exec(s.slice(from))?.[0] ?? s[from]!;
  let i = from + name.length;
  let j = i; while (j < s.length && /\s/.test(s[j]!)) j++;
  if (s[j] !== "=") return { attr: { name: name.toLowerCase(), value: "", raw: "", quote: "" }, end: i };
  j++; while (j < s.length && /\s/.test(s[j]!)) j++;
  const q = s[j];
  if (q === '"' || q === "'") {
    const endQ = s.indexOf(q, j + 1);
    if (endQ < 0) return null;
    const raw = s.slice(j + 1, endQ);
    return { attr: { name: name.toLowerCase(), value: decodeEntities(raw), raw, quote: q }, end: endQ + 1 };
  }
  let k = j; while (k < s.length && !/[\s>]/.test(s[k]!)) k++;
  const raw = s.slice(j, k);
  return { attr: { name: name.toLowerCase(), value: decodeEntities(raw), raw, quote: "" }, end: k };
}

/** where the hidden content of `<name>` ends: after its closing tag, or at the end of the text */
function closingEnd(s: string, from: number, name: string): number {
  const re = new RegExp(`</${name}[\\s/>]`, "ig"); re.lastIndex = from;
  const m = re.exec(s);
  if (!m) return s.length;
  const gt = s.indexOf(">", m.index);
  return gt < 0 ? s.length : gt + 1;
}

const NAMED: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: " ", copy: "©", reg: "®", trade: "™", hellip: "…", ndash: "–", mdash: "—", middot: "·", bull: "•", laquo: "«", raquo: "»", lsquo: "‘", rsquo: "’", ldquo: "“", rdquo: "”", euro: "€" };
/** the characters an HTML text stands for: `&amp;` is "&", `&#x09;` a tab; a lone "&" or an unknown name stays as typed */
export function decodeEntities(s: string): string {
  if (!s.includes("&")) return s;
  return s.replace(/&(#[xX][0-9a-fA-F]{1,6}|#[0-9]{1,7}|[A-Za-z][A-Za-z0-9]{1,31});?/g, (whole, body: string) => {
    if (body[0] === "#") {
      const code = body[1] === "x" || body[1] === "X" ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
      return code > 0 && code <= 0x10ffff && !(code >= 0xd800 && code <= 0xdfff) ? String.fromCodePoint(code) : "�";
    }
    const named = NAMED[body];
    return named !== undefined && whole.endsWith(";") ? named : whole;
  });
}

/** text made safe to stand in HTML, in text or in a quoted attribute */
export const escapeHtml = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
